/**
 * <fitment-grid-filter> - "only parts for your car" filter for collection and search grids.
 *
 * Two surfaces, two mechanisms, one pill:
 *
 *   COLLECTION (surface="collection") - SERVER-SIDE. Navigates to Shopify's tag-filtered collection
 *     URL (/collections/<handle>/<chassis-tag>) so the server returns the complete fitting set,
 *     correctly paginated and counted, at any collection size. Client-side hiding can only filter
 *     the loaded page, which is wrong on big collections (a 1499-item hub shows 2 of 4 until you
 *     scroll), so collections do not client-filter. The shopper's on/off choice is sticky across
 *     collections via localStorage.
 *
 *   SEARCH (surface="search") - CLIENT-SIDE. Native Shopify search has no fitment facet and no tag
 *     URL to navigate to (Algolia was never connected), and result sets are smaller and
 *     intent-driven, so here we hide non-fitting cards in the rendered grid. Re-applies on connect,
 *     on `vehicle:change`, and on grid mutations (infinite-scroll appends).
 *
 * Crawl-safe either way: filtering needs a saved car in localStorage, which no crawler and no
 * first-time visitor has, so the server-rendered full page is what Google and organic landings see.
 *
 * Fitment truth: the active car's chassis tag comes from parts_finder_data.json (collection_url ->
 * tag), the same source the PDP fitment verdict and the parts-finder use. On search, each grid <li>
 * carries data-fitment-tags (chassis tags + a universal-fit marker, from fitment-card-attrs.liquid).
 *
 * Mode (data-mode): 'auto' filters as soon as a car is saved; 'opt_in' waits for the shopper to
 * switch it on from the bar; 'off' is handled in Liquid (the element is not rendered).
 */

import { getActive } from '@theme/garage-store';

const MODE_KEY = 'revzone:fitment-mode'; // 'on' | 'off' - the shopper's explicit choice
const LEGACY_KEY = 'revzone:fitment-filter'; // retired opt-in chip flag; cleared on sight
const HIDDEN_CLASS = 'fitment-grid-filter--hidden';

let vehiclesPromise = null;
function loadVehicles(url) {
  if (!vehiclesPromise) {
    vehiclesPromise = fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => (d && Array.isArray(d.vehicles) ? d.vehicles : []))
      .catch(() => []);
  }
  return vehiclesPromise;
}

// Tag -> URL handle, matching Shopify's tag-filter URLs (AUDI_8L -> audi_8l,
// PORSCHE_991.1 -> porsche_991-1). Verified against live tag-filter URLs.
function tagToHandle(tag) {
  return String(tag)
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

class FitmentGridFilter extends HTMLElement {
  #surface = 'search';
  #mode = 'auto';
  /** @type {HTMLElement | null} */ #bar = null;
  /** @type {Element | null} */ #grid = null;
  /** @type {MutationObserver | null} */ #observer = null;
  #hasCar = false;
  /** @type {string | null} */ #tag = null; // active car chassis tag, lowercased
  #label = '';
  #url = ''; // active car chassis-collection url
  #barSig = '';
  // collection-surface data
  #collectionUrl = '';
  #collectionHandle = '';
  /** @type {string[]} */ #currentTags = [];
  #isVehicleColl = false;

  connectedCallback() {
    try {
      localStorage.removeItem(LEGACY_KEY);
    } catch {
      /* private mode */
    }

    this.#surface = this.dataset.surface || 'search';
    this.#mode = this.dataset.mode || 'auto';
    this.#bar = this.querySelector('.fitment-grid-filter__bar');
    this.#collectionUrl = this.dataset.collectionUrl || '';
    this.#collectionHandle = this.dataset.collectionHandle || '';
    this.#currentTags = (this.dataset.currentTags || '')
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);

    document.addEventListener('vehicle:change', this.#refresh);
    this.addEventListener('click', this.#onClick);

    if (this.#surface === 'search') {
      const root = this.closest('results-list') || document;
      this.#grid = root.querySelector('[ref="grid"]') || root.querySelector('.collection-wrapper');
      if (this.#grid) {
        this.#observer = new MutationObserver((mutations) => {
          if (mutations.some((m) => m.addedNodes.length)) this.#applySearch();
        });
        this.#observer.observe(this.#grid, { childList: true });
      }
    }

    this.#refresh();
  }

  disconnectedCallback() {
    document.removeEventListener('vehicle:change', this.#refresh);
    this.#observer?.disconnect();
  }

  #choice() {
    try {
      return localStorage.getItem(MODE_KEY);
    } catch {
      return null;
    }
  }

  #setChoice(value) {
    try {
      localStorage.setItem(MODE_KEY, value);
    } catch {
      /* private mode - holds for this page only */
    }
  }

  /** Does the shopper want fitting-only results right now? */
  #wantFilter() {
    if (this.#mode === 'opt_in') return this.#choice() === 'on';
    return this.#choice() !== 'off'; // 'auto': on unless they chose Show all
  }

  #refresh = async () => {
    const active = getActive();
    this.#hasCar = !!(active && active.url);

    if (!this.#hasCar) {
      this.#tag = null;
      this.#label = '';
      this.#url = '';
      this.#after();
      return;
    }

    this.#label = active.label || '';
    this.#url = active.url;

    const vehicles = await loadVehicles(this.dataset.dataUrl);
    const mine = vehicles.find((v) => v.collection_url === active.url);
    this.#tag = mine && mine.tag ? mine.tag.toLowerCase() : null;
    if (!this.#label && mine) this.#label = mine.model_display || '';
    // On a vehicle (chassis) collection every part already fits - never filter there.
    this.#isVehicleColl = vehicles.some((v) => v.collection_handle === this.#collectionHandle);

    this.#after();
  };

  #after() {
    if (this.#surface === 'collection') this.#applyCollection();
    else this.#applySearch();
  }

  // --- Collection: server-side tag-URL navigation ---------------------------------------------
  #applyCollection() {
    if (!this.#hasCar || !this.#tag || this.#isVehicleColl) {
      this.#hideBar();
      return;
    }

    const isFiltered = this.#currentTags.includes(this.#tag);
    // Respect a manual, different tag filter rather than hijacking it.
    if (this.#currentTags.length > 0 && !isFiltered) {
      this.#hideBar();
      return;
    }

    const want = this.#wantFilter();
    // Never auto-navigate inside the theme editor - it would yank the merchant's preview around.
    const inEditor = !!(window.Shopify && window.Shopify.designMode);

    if (!inEditor) {
      if (want && !isFiltered) {
        location.replace(`${this.#collectionUrl}/${tagToHandle(this.#tag)}${location.search}`);
        return;
      }
      if (!want && isFiltered) {
        location.replace(`${this.#collectionUrl}${location.search}`);
        return;
      }
    }

    this.#renderBar(want ? 'on' : 'off');
  }

  // --- Search: client-side card hiding --------------------------------------------------------
  #applySearch() {
    if (!this.#grid) return;
    const filtering = this.#hasCar && !!this.#tag && this.#wantFilter();
    let visible = 0;

    this.#grid.querySelectorAll('.product-grid__item').forEach((li) => {
      if (!filtering) {
        li.classList.remove(HIDDEN_CLASS);
        visible++;
        return;
      }
      const tags = (li.getAttribute('data-fitment-tags') || '').split(/\s+/);
      const fits = tags.includes('universal-fit') || tags.includes(this.#tag);
      li.classList.toggle(HIDDEN_CLASS, !fits);
      if (fits) visible++;
    });

    if (!this.#hasCar || !this.#tag) {
      this.#hideBar();
      return;
    }
    this.#renderBar(filtering ? (visible > 0 ? 'on' : 'empty') : 'off');
  }

  // --- Pill -----------------------------------------------------------------------------------
  #renderBar(state) {
    if (!this.#bar) return;
    const sig = `${state}|${this.#label}|${this.#url}`;
    if (sig === this.#barSig && !this.#bar.hidden) return;
    this.#barSig = sig;

    this.#bar.hidden = false;
    this.#bar.dataset.state = state;
    this.#bar.querySelectorAll('[data-fitment-label]').forEach((el) => {
      el.textContent = this.#label;
    });
    this.#bar.querySelectorAll('[data-fitment-catalogue]').forEach((a) => {
      a.setAttribute('href', this.#url);
    });
  }

  #hideBar() {
    if (!this.#bar || this.#bar.hidden) return;
    this.#bar.hidden = true;
    this.#barSig = '';
  }

  #onClick = (event) => {
    const action = event.target.closest('[data-fitment-action]');
    if (!action) return;
    event.preventDefault();
    switch (action.dataset.fitmentAction) {
      case 'change-vehicle':
        this.#openGarage();
        return;
      case 'show-all':
        this.#setChoice('off');
        break;
      case 'show-mine':
        this.#setChoice('on');
        break;
      default:
        return;
    }
    this.#after(); // collection -> navigates; search -> re-applies in place
  };

  /** Open the header "My Garage" modal so the shopper can switch cars without leaving the page. */
  #openGarage() {
    const triggers = [...document.querySelectorAll('.vehicle-garage__trigger')];
    const target = triggers.find((t) => t.offsetParent !== null) || triggers[0];
    if (target instanceof HTMLElement) target.click();
  }
}

if (!customElements.get('fitment-grid-filter')) {
  customElements.define('fitment-grid-filter', FitmentGridFilter);
}
